'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import ChevronDown from '@/components/icons/chevron-down'
import TrophyIcon from '@/components/icons/trophy'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import getUserName from '@/helpers/get-user-name'
import { urlSafeProfilePath } from '@/helpers/url-safe-profile-path'
import { getUserProfilePictureUrl } from '@/helpers/user'
import dayjs from '@/lib/dayjs'
import Link from 'next/link'
import { useState } from 'react'

export default function MembersSection({
  owner,
  communityMembers,
  communityMembersLoading,
}) {
  const [filterOption, setFilterOption] = useState('Members')
  const filterDropDownOptions = ['Members', 'Admins']
  const [filterDropDownOpen, setFilterDropDownOpen] = useState(false)
  const [filterHasBeenInteractedWith, setFilterHasBeenInteractedWith] =
    useState(false)

  return (
    <div className="w-full">
      {communityMembersLoading ? (
        <Skeleton className="mt-6 h-[400px] w-full" />
      ) : !communityMembers || communityMembers.length == 0 ? (
        <div className="flex items-center border border-neutral-blue-700 bg-neutral-blue-800">
          <h5 className="m-auto text-center">This community has no members</h5>
        </div>
      ) : (
        <div className="indented-grid">
          {/* Filter for mobile */}
          <div className="relative col-span-full w-full">
            <div className="relative z-20 mt-6 flex w-full min-w-[200px] flex-col items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-6 py-2 align-middle desktop-xs:hidden">
              <div
                className="flex cursor-pointer items-center justify-between align-middle"
                onClick={() => {
                  setFilterDropDownOpen(!filterDropDownOpen)
                }}
              >
                <div className="text-p-1">{filterOption}</div>
                <ChevronDown className="ml-2 h-4 w-4 stroke-white" />
              </div>

              {filterDropDownOpen === true && (
                <div className="absolute -left-px mt-[23px] flex w-[calc(100%+2px)] flex-col rounded-b-sm border-b border-l border-r border-neutral-blue-700 bg-neutral-blue-800 px-2 py-2">
                  {filterDropDownOptions.map((option, index) => (
                    <div
                      className="truncate-1 ml-0 mr-auto w-full cursor-pointer break-all rounded-sm px-2 py-1 text-p-1 transition-all ease-in-out hover:bg-neutral-blue-900"
                      onClick={() => {
                        setFilterOption(option)
                        setFilterDropDownOpen(false)
                        setFilterHasBeenInteractedWith(true)
                      }}
                      key={index}
                    >
                      ‎ {option}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="col-span-full hidden h-[70px] pb-4 pt-10 text-[12px] font-semibold leading-[14px] tracking-[4px] text-neutral-blue-500 desktop-xs:col-span-6 desktop-xs:grid desktop-xs:grid-cols-6 desktop-sm:col-span-7 desktop-sm:grid-cols-7">
            <p className="col-span-2">NAME</p>
            <p className="col-span-2">JOINED</p>
          </div>
          <div className="col-span-full hidden h-[70px] desktop-xs:col-span-2 desktop-xs:block desktop-sm:col-span-3" />

          <div
            className={`${filterOption === 'Admins' && filterHasBeenInteractedWith ? 'hidden' : ''} desktop-xs:block! col-span-full mt-6 desktop-xs:col-span-6 desktop-xs:mt-0 desktop-sm:col-span-7`}
          >
            {communityMembers
              .filter((member) => member.role === 'driver')
              .map((member) => (
                <Link
                  className="group col-span-full -ml-5 flex items-center justify-between gap-3 rounded-md py-4 pl-5 pr-[14px] align-middle font-inter transition-all ease-in-out desktop-xs:grid desktop-xs:grid-cols-6 desktop-xs:hover:bg-neutral-blue-700 desktop-sm:grid-cols-7"
                  key={member.userId}
                  href={urlSafeProfilePath({
                    ...member.user,
                    id: member.userId,
                    profileType: 'driver',
                  })}
                  target="_blank"
                >
                  <div className="col-span-2 flex items-center gap-3 align-middle">
                    <div className="relative aspect-square w-10 min-w-10 overflow-hidden rounded-full">
                      <ImageWithFallback
                        src={getUserProfilePictureUrl(member.user)}
                        alt="A users profile picture"
                        fill
                      />
                    </div>
                    <div className="flex flex-col">
                      <p className="truncate-1 break-all pb-[2px] text-p-1 font-normal desktop-xs:text-p-2">
                        {member.user.username}
                      </p>
                      <p className="text-p-1 font-normal text-neutral-blue-200 desktop-xs:hidden">
                        Joined: {dayjs(member.createTs).format('DD MMM YYYY')}
                      </p>
                    </div>
                  </div>
                  <div className="col-span-2 hidden desktop-xs:block">
                    {dayjs(member.createTs).format('DD MMMM YYYY')}
                  </div>
                  <div className="col-span-1 hidden desktop-sm:block"></div>
                  <div className="col-span-2 flex">
                    <Button
                      variant="outline"
                      asChild
                      className="bg-neutral-blue-900! desktop-xs:group-hover:bg-white! desktop-xs:group-hover:text-black! ml-auto mr-0 py-2 font-inter text-p-1 transition-all ease-in-out desktop-xs:py-3"
                    >
                      <p>
                        View
                        <span className="hidden tablet:inline-block">
                          &nbsp;profile
                        </span>
                      </p>
                    </Button>
                  </div>
                </Link>
              ))}
          </div>
          <div
            className={`${filterOption === 'Members' && filterHasBeenInteractedWith ? 'hidden' : ''} desktop-xs:block! col-span-full mt-6 desktop-xs:col-span-2 desktop-xs:mt-0 desktop-sm:col-span-3`}
          >
            <div className="w-full rounded-lg border border-neutral-blue-700 bg-neutral-blue-800 pb-[34px]">
              <h5 className="font-semi mx-10 mb-[14px] mt-8 text-h-5">
                Admins
              </h5>
              {owner && (
                <Link
                  className="group mx-7 flex items-center justify-between rounded-md px-3 py-[6px] align-middle transition-all ease-in-out hover:bg-neutral-blue-700"
                  href={urlSafeProfilePath({
                    ...owner,
                    profileType: 'driver',
                  })}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative aspect-square w-10 min-w-10 overflow-hidden rounded-full">
                      <ImageWithFallback
                        src={getUserProfilePictureUrl(owner, {
                          profile: 'driver',
                        })}
                        alt="A users profile picture"
                        fill
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="truncate-1 pb-[2px] text-p-2 font-normal">
                        {getUserName({ user: owner }, { profile: 'driver' })}
                      </p>
                      <TrophyIcon className="size-5 stroke-[#FF6100]" />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    asChild
                    className="bg-neutral-blue-900! group-hover:bg-white! group-hover:text-black! ml-auto mr-0 py-2 font-inter text-p-1 transition-all ease-in-out desktop-xs:hidden desktop-xs:py-3"
                  >
                    <p>
                      View
                      <span className="hidden tablet:inline-block">
                        &nbsp;profile
                      </span>
                    </p>
                  </Button>
                </Link>
              )}
              {communityMembers
                .filter((member) => member.role === 'admin')
                .map((member, index) => (
                  <Link
                    className="group mx-7 flex items-center justify-between rounded-md px-3 py-[6px] align-middle transition-all ease-in-out hover:bg-neutral-blue-700"
                    key={index}
                    href={urlSafeProfilePath({
                      ...member.user,
                      id: member.userId,
                      profileType: 'driver',
                    })}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative aspect-square w-10 min-w-10 overflow-hidden rounded-full">
                        <ImageWithFallback
                          src={getUserProfilePictureUrl(member.user, {
                            profile: 'driver',
                          })}
                          alt="A users profile picture"
                          fill
                        />
                      </div>
                      <p className="truncate-1 pb-[2px] text-p-2 font-normal">
                        {getUserName(
                          { user: member.user },
                          { profile: 'driver' },
                        )}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      asChild
                      className="bg-neutral-blue-900! group-hover:bg-white! group-hover:text-black! ml-auto mr-0 py-2 font-inter text-p-1 transition-all ease-in-out desktop-xs:hidden desktop-xs:py-3"
                    >
                      <p>
                        View
                        <span className="hidden tablet:inline-block">
                          &nbsp;profile
                        </span>
                      </p>
                    </Button>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
